import React, { useEffect, useState } from "react";
import {
    PlusIcon,
    XMarkIcon,
    CheckCircleIcon,
    MapPinIcon,
    TrashIcon,
    PencilSquareIcon,
    ExclamationTriangleIcon,
    BuildingStorefrontIcon,
} from "@heroicons/react/24/outline";
import { useCollectionPoints } from "../hooks/useCollectionPoints";
import { useStores } from "../hooks/useStores";
import type { Store, CollectionPoint } from "../types";

type PointForm = {
    name: string;
    address_line1: string;
    suburb: string;
    city: string;
    latitude: string;
    longitude: string;
    instructions: string;
};

const EMPTY_FORM: PointForm = {
    name: "",
    address_line1: "",
    suburb: "",
    city: "",
    latitude: "",
    longitude: "",
    instructions: "",
};

const Spinner = () => (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
);

export default function CollectionPointsPage() {
    const {
        points,
        loading,
        error: pointsError,
        fetchPoints,
        createPoint,
        updatePoint,
        toggleActive: togglePointActive,
        deletePoint,
    } = useCollectionPoints();

    const { stores, fetchStores } = useStores();

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState<PointForm>(EMPTY_FORM);
    const [creating, setCreating] = useState(false);
    const [createSuccess, setCreateSuccess] = useState("");
    const [createError, setCreateError] = useState("");

    const [editPoint, setEditPoint] = useState<CollectionPoint | null>(null);
    const [editForm, setEditForm] = useState<PointForm>(EMPTY_FORM);
    const [editing, setEditing] = useState(false);
    const [editSuccess, setEditSuccess] = useState("");
    const [editError, setEditError] = useState("");

    const [deleteTarget, setDeleteTarget] = useState<CollectionPoint | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        void Promise.all([fetchPoints(), fetchStores()]);
    }, [fetchPoints, fetchStores]);

    const openCreate = (prefill?: PointForm) => {
        setCreateForm(prefill ?? EMPTY_FORM);
        setCreateSuccess("");
        setCreateError("");
        setIsCreateOpen(true);
    };

    const openCreateFromStore = (store: Store) => {
        openCreate({
            name: store.name,
            address_line1: store.address_line1 ?? "",
            suburb: store.suburb ?? "",
            city: store.city ?? "",
            latitude: store.latitude != null ? String(store.latitude) : "",
            longitude: store.longitude != null ? String(store.longitude) : "",
            instructions: "",
        });
    };

    const submitCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreating(true);
        setCreateError("");

        const payload = {
            name: createForm.name.trim(),
            address_line1: createForm.address_line1.trim(),
            suburb: createForm.suburb.trim() || null,
            city: createForm.city.trim() || null,
            latitude: createForm.latitude ? parseFloat(createForm.latitude) : null,
            longitude: createForm.longitude ? parseFloat(createForm.longitude) : null,
            instructions: createForm.instructions.trim() || null,
            is_active: true,
        };

        try {
            const created = await createPoint(payload);

            if (!created) {
                setCreateError(pointsError ?? "Failed to create collection point.");
                return;
            }

            setCreateSuccess("Collection point created successfully!");

            setTimeout(() => {
                setIsCreateOpen(false);
                setCreateSuccess("");
            }, 1500);
        } catch (err) {
            setCreateError(err instanceof Error ? err.message : "Failed to create collection point.");
        } finally {
            setCreating(false);
        }
    };

    const openEdit = (point: CollectionPoint) => {
        setEditPoint(point);
        setEditForm({
            name: point.name,
            address_line1: point.address_line1,
            suburb: point.suburb ?? "",
            city: point.city ?? "",
            latitude: point.latitude != null ? String(point.latitude) : "",
            longitude: point.longitude != null ? String(point.longitude) : "",
            instructions: point.instructions ?? "",
        });
        setEditSuccess("");
        setEditError("");
    };

    const submitEdit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!editPoint) return;

        setEditing(true);
        setEditError("");

        const patch = {
            name: editForm.name.trim(),
            address_line1: editForm.address_line1.trim(),
            suburb: editForm.suburb.trim() || null,
            city: editForm.city.trim() || null,
            latitude: editForm.latitude ? parseFloat(editForm.latitude) : null,
            longitude: editForm.longitude ? parseFloat(editForm.longitude) : null,
            instructions: editForm.instructions.trim() || null,
        };

        try {
            const updated = await updatePoint(editPoint.id, patch);

            if (!updated) {
                setEditError(pointsError ?? "Failed to update collection point.");
                return;
            }

            setEditSuccess("Collection point updated!");

            setTimeout(() => {
                setEditPoint(null);
                setEditSuccess("");
            }, 1500);
        } catch (err) {
            setEditError(err instanceof Error ? err.message : "Failed to update collection point.");
        } finally {
            setEditing(false);
        }
    };

    const toggleActive = async (point: CollectionPoint) => {
        await togglePointActive(point);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;

        setDeleting(true);

        try {
            const deleted = await deletePoint(deleteTarget.id);

            if (deleted) {
                setDeleteTarget(null);
            }
        } finally {
            setDeleting(false);
        }
    };

    const fieldClass =
        "block w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:ring-2 focus:ring-[#e6186c] focus:border-[#e6186c] outline-none transition-all";

    const renderForm = (
        form: PointForm,
        setForm: React.Dispatch<React.SetStateAction<PointForm>>,
        successMsg: string,
        errorMsg: string
    ) => (
        <div className="mt-5 space-y-4">
            {successMsg && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm border border-green-200">
                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                    {successMsg}
                </div>
            )}

            {errorMsg && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                    {errorMsg}
                </div>
            )}

            <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                    Collection Point Name <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Main Store Front, Locker Bay A, Mall Kiosk"
                    className={fieldClass}
                />
            </div>

            <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                    Street Address <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    required
                    value={form.address_line1}
                    onChange={(e) => setForm((p) => ({ ...p, address_line1: e.target.value }))}
                    placeholder="e.g. 12 Main Road"
                    className={fieldClass}
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">Suburb</label>
                    <input
                        type="text"
                        value={form.suburb}
                        onChange={(e) => setForm((p) => ({ ...p, suburb: e.target.value }))}
                        placeholder="e.g. Sandton"
                        className={fieldClass}
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">City</label>
                    <input
                        type="text"
                        value={form.city}
                        onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                        placeholder="e.g. Johannesburg"
                        className={fieldClass}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                        Latitude <span className="text-stone-400 font-normal">(optional)</span>
                    </label>
                    <input
                        type="number"
                        step="any"
                        value={form.latitude}
                        onChange={(e) => setForm((p) => ({ ...p, latitude: e.target.value }))}
                        placeholder="e.g. -26.1052"
                        className={fieldClass}
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                        Longitude <span className="text-stone-400 font-normal">(optional)</span>
                    </label>
                    <input
                        type="number"
                        step="any"
                        value={form.longitude}
                        onChange={(e) => setForm((p) => ({ ...p, longitude: e.target.value }))}
                        placeholder="e.g. 28.0560"
                        className={fieldClass}
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">
                    Collection Instructions{" "}
                    <span className="text-stone-400 font-normal">(optional)</span>
                </label>
                <textarea
                    value={form.instructions}
                    onChange={(e) => setForm((p) => ({ ...p, instructions: e.target.value }))}
                    rows={3}
                    placeholder="e.g. Enter through the side gate on Smith Street. Ring the bell and show your order reference."
                    className={`${fieldClass} resize-none`}
                />
                <p className="mt-1 text-xs text-stone-400">
                    Shown to the customer when their order is ready for collection.
                </p>
            </div>
        </div>
    );

    const activePoints = points.filter((p) => p.is_active);
    const inactivePoints = points.filter((p) => !p.is_active);

    const existingNames = new Set(points.map((p) => p.name.toLowerCase().trim()));
    const storesNotAdded: Store[] = stores.filter(
        (s) => !existingNames.has(s.name.toLowerCase().trim())
    );

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-semibold text-stone-900">Collection Points</h2>
                    <p className="text-sm text-stone-500 mt-1">
                        Manage pickup locations shown to customers at checkout
                    </p>
                </div>

                <button
                    onClick={() => openCreate()}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#e6186c] text-white text-sm font-medium hover:bg-[#d01560] transition-colors whitespace-nowrap"
                >
                    <PlusIcon className="h-4 w-4" />
                    Add Collection Point
                </button>
            </div>

            {pointsError && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                    {pointsError}
                </div>
            )}

            {loading ? (
                <div className="py-12 text-center text-stone-400 text-sm">
                    Loading collection points...
                </div>
            ) : (
                <div className="space-y-8">
                    {storesNotAdded.length > 0 && (
                        <div>
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-1">
                                Add from your stores
                            </h3>
                            <p className="text-xs text-stone-400 mb-3">
                                Click a store to pre-fill the form with its details.
                            </p>

                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {storesNotAdded.map((store) => (
                                    <button
                                        key={store.id}
                                        type="button"
                                        onClick={() => openCreateFromStore(store)}
                                        className="rounded-xl border border-dashed border-stone-300 bg-white p-5 text-left hover:border-[#e6186c] hover:bg-pink-50 transition-all group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-xl bg-stone-100 group-hover:bg-pink-100 flex items-center justify-center flex-shrink-0 transition-colors">
                                                <BuildingStorefrontIcon className="h-5 w-5 text-stone-400 group-hover:text-[#e6186c] transition-colors" />
                                            </div>

                                            <div className="min-w-0">
                                                <p className="font-semibold text-stone-900 text-sm truncate">
                                                    {store.name}
                                                </p>
                                                <p className="text-xs text-stone-400 mt-0.5">
                                                    {[store.address_line1, store.suburb].filter(Boolean).join(", ") ||
                                                        "No address on record"}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {points.length === 0 && (
                        <div className="rounded-xl border-2 border-dashed border-stone-200 bg-white p-12 text-center">
                            <MapPinIcon className="h-10 w-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-sm font-semibold text-stone-600">No collection points yet</p>
                            <p className="text-xs text-stone-400 mt-1 mb-4">
                                Add a collection point so customers can select where to pick up their order.
                            </p>

                            <button
                                onClick={() => openCreate()}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#e6186c] text-white text-sm font-medium hover:bg-[#d01560] transition-colors"
                            >
                                <PlusIcon className="h-4 w-4" />
                                Add First Collection Point
                            </button>
                        </div>
                    )}

                    {activePoints.length > 0 && (
                        <div>
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-3">
                                Active — {activePoints.length}{" "}
                                {activePoints.length === 1 ? "point" : "points"}
                            </h3>

                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {activePoints.map((point) => (
                                    <PointCard
                                        key={point.id}
                                        point={point}
                                        onEdit={() => openEdit(point)}
                                        onToggle={() => toggleActive(point)}
                                        onDelete={() => setDeleteTarget(point)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {inactivePoints.length > 0 && (
                        <div>
                            <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">
                                Inactive — hidden from customers
                            </h3>

                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                                {inactivePoints.map((point) => (
                                    <PointCard
                                        key={point.id}
                                        point={point}
                                        onEdit={() => openEdit(point)}
                                        onToggle={() => toggleActive(point)}
                                        onDelete={() => setDeleteTarget(point)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {isCreateOpen && (
                <Modal
                    title="Add Collection Point"
                    subtitle="This point will be visible to customers at checkout"
                    onClose={() => setIsCreateOpen(false)}
                >
                    <form onSubmit={submitCreate}>
                        {renderForm(createForm, setCreateForm, createSuccess, createError)}

                        <ModalFooter
                            onCancel={() => setIsCreateOpen(false)}
                            loading={creating}
                            success={!!createSuccess}
                            submitLabel="Add Collection Point"
                        />
                    </form>
                </Modal>
            )}

            {editPoint && (
                <Modal
                    title="Edit Collection Point"
                    subtitle={editPoint.name}
                    onClose={() => setEditPoint(null)}
                >
                    <form onSubmit={submitEdit}>
                        {renderForm(editForm, setEditForm, editSuccess, editError)}

                        <ModalFooter
                            onCancel={() => setEditPoint(null)}
                            loading={editing}
                            success={!!editSuccess}
                            submitLabel="Save Changes"
                        />
                    </form>
                </Modal>
            )}

            {deleteTarget && (
                <Modal
                    title="Remove Collection Point"
                    subtitle="This cannot be undone"
                    onClose={() => setDeleteTarget(null)}
                >
                    <div className="mt-5">
                        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                            <p className="font-semibold mb-1">
                                Are you sure you want to remove this collection point?
                            </p>

                            <p className="font-mono text-xs bg-red-100 rounded px-2 py-1 mt-2 inline-block">
                                {deleteTarget.name} — {deleteTarget.address_line1}
                            </p>

                            <p className="mt-2 text-xs text-red-600">
                                Existing orders referencing this point will keep their record, but it
                                will no longer be selectable for new orders.
                            </p>
                        </div>

                        <div className="flex justify-end gap-3 pt-5 border-t border-stone-100 mt-5">
                            <button
                                type="button"
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <Spinner /> Removing...
                                    </>
                                ) : (
                                    <>
                                        <TrashIcon className="h-4 w-4" /> Remove
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function PointCard({
                       point,
                       onEdit,
                       onToggle,
                       onDelete,
                   }: {
    point: CollectionPoint;
    onEdit: () => void;
    onToggle: () => void;
    onDelete: () => void;
}) {
    const address = [point.address_line1, point.suburb, point.city]
        .filter(Boolean)
        .join(", ");

    return (
        <div className="rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
            <div className={`h-1 w-full ${point.is_active ? "bg-[#e6186c]" : "bg-stone-300"}`} />

            <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div
                            className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                point.is_active ? "bg-pink-50" : "bg-stone-100"
                            }`}
                        >
                            <MapPinIcon
                                className={`h-5 w-5 ${
                                    point.is_active ? "text-[#e6186c]" : "text-stone-400"
                                }`}
                            />
                        </div>

                        <div className="min-w-0">
                            <h4 className="font-semibold text-stone-900 text-sm truncate">
                                {point.name}
                            </h4>
                            <p className="text-xs text-stone-500 mt-0.5 truncate">{address}</p>
                        </div>
                    </div>

                    <span
                        className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            point.is_active
                                ? "bg-pink-100 text-[#e6186c]"
                                : "bg-stone-100 text-stone-500"
                        }`}
                    >
                        {point.is_active ? "ACTIVE" : "INACTIVE"}
                    </span>
                </div>

                {point.instructions && (
                    <p className="text-xs text-stone-500 bg-stone-50 rounded-lg px-3 py-2 mb-3 line-clamp-2">
                        {point.instructions}
                    </p>
                )}

                {point.latitude && point.longitude && (
                    <div className="flex items-center gap-1.5 text-xs text-stone-400 mb-3">
                        <MapPinIcon className="h-3 w-3" />
                        <span>
                            {Number(point.latitude).toFixed(4)},{" "}
                            {Number(point.longitude).toFixed(4)}
                        </span>
                    </div>
                )}

                <div className="pt-3 border-t border-stone-100 flex items-center gap-2">
                    <button
                        onClick={onEdit}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 text-stone-600 text-xs font-medium hover:bg-stone-50 transition-colors"
                    >
                        <PencilSquareIcon className="h-3.5 w-3.5" />
                        Edit
                    </button>

                    <button
                        onClick={onToggle}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                            point.is_active
                                ? "border-amber-200 text-amber-600 hover:bg-amber-50"
                                : "border-pink-200 text-[#e6186c] hover:bg-pink-50"
                        }`}
                    >
                        {point.is_active ? "Deactivate" : "Activate"}
                    </button>

                    <button
                        onClick={onDelete}
                        className="p-1.5 rounded-lg border border-stone-200 text-stone-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors"
                        title="Remove collection point"
                    >
                        <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function Modal({
                   title,
                   subtitle,
                   onClose,
                   children,
               }: {
    title: string;
    subtitle: string;
    onClose: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
                <div className="h-1 bg-[#e6186c] w-full flex-shrink-0" />

                <div className="p-6 overflow-y-auto flex-1">
                    <div className="flex items-center justify-between mb-1">
                        <div>
                            <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
                            <p className="text-xs text-stone-500 mt-0.5">{subtitle}</p>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>

                    {children}
                </div>
            </div>
        </div>
    );
}

function ModalFooter({
                         onCancel,
                         loading,
                         success,
                         submitLabel,
                     }: {
    onCancel: () => void;
    loading: boolean;
    success: boolean;
    submitLabel: string;
}) {
    return (
        <div className="flex justify-end gap-3 pt-5 border-t border-stone-100 mt-5">
            <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-50"
            >
                Cancel
            </button>

            <button
                type="submit"
                disabled={loading || success}
                className="px-4 py-2.5 rounded-lg bg-[#e6186c] text-white text-sm font-medium hover:bg-[#d01560] disabled:opacity-60 flex items-center gap-2"
            >
                {loading ? (
                    <>
                        <Spinner /> Saving...
                    </>
                ) : success ? (
                    <>
                        <CheckCircleIcon className="h-4 w-4" /> Saved!
                    </>
                ) : (
                    submitLabel
                )}
            </button>
        </div>
    );
}